/**
 * Plan de salle v2 (SPEC FC2, FC3) : carte « Choisissez votre spot », phrase au-dessus du plan,
 * grille Pick-a-Spot réelle (lib/room.ts), légende conditionnelle, notes v1 conservées.
 * Spots = <button> : un spot pris, à moi ou hors sélection est `disabled` (non activable au clavier).
 *
 * Clavier (revue du 04/10 : 37 à 52 tabulations pour traverser le plan) : un seul arrêt de tabulation, le spot choisi
 * ou à défaut le premier spot libre (tabindex mobile) ; flèches gauche / droite = spot libre précédent / suivant dans
 * l'ordre de lecture, haut / bas = spot libre le plus proche dans la rangée du dessus / du dessous, Début / Fin =
 * premier / dernier ; Entrée ou Espace choisit (bouton natif). Chaque spot garde son nom accessible.
 *
 * Au doigt (retour de Daphné du 07/10 : « spots trop petits au doigt ») : sur téléphone, spots de 36 px au moins et
 * zone active de 44 px (class.css) ; un plan plus large que l'écran défile horizontalement. Il est alors centré à
 * l'arrivée (sur le spot choisi ou réservé s'il y en a un, sinon sur le milieu de la salle : un fondu à chaque bord
 * montre qu'il y a des spots des deux côtés), une phrase invite à le faire glisser, et un spot choisi à moitié caché
 * (sous le fondu) est ramené au centre.
 */
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { centeredScrollLeft, doubleBenchNote, spotLabel, type PlanCell, type PlanModel } from '../lib/room';
import { Icon } from './Icon';

export interface RoomPlanProps {
  plan: PlanModel;
  coachName: string;
  kicker: string;
  title: string;
  /** Phrase au-dessus du plan (SPEC FC2 : « Sélectionnez votre spot pour confirmer votre réservation. »). */
  hint?: ReactNode;
  /** Étiquette de places (libres / complet). */
  capacityTag?: { label: string; tone: 'green' | 'amber' | 'red' } | null;
  /** Les spots libres sont activables. */
  interactive: boolean;
  onSpot?: (spotNumber: number) => void;
  showGuestLegend: boolean;
  /** Légende « Vous » (spot choisi) : seulement quand le client choisit SON spot (pas en ajout d'invité). */
  showSelfLegend?: boolean;
  /** Note Dynamo du premier rang (= Class/index.tsx, site 5736526). */
  frontRowNote?: string | null;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const LEGEND_SHAPES = [
  { key: 'bag', label: 'Sac' },
  { key: 'bench', label: 'Banc' },
  { key: 'double', label: 'Banc double — sans accès direct au sac' },
];

export const RoomPlan = forwardRef<HTMLDivElement, RoomPlanProps>(function RoomPlan(
  { plan, coachName, kicker, title, hint, capacityTag, interactive, onSpot, showGuestLegend, showSelfLegend = interactive, frontRowNote },
  ref,
) {
  const note = doubleBenchNote(plan.doubleBenchSpots);
  const hasMine = plan.mySpots.length > 0;
  const [focusedSpot, setFocusedSpot] = useState<number | null>(null);

  /*
   * Plan plus large que sa carte : bord(s) qui cachent encore des spots, en attribut (fondu, class.css). Attribut posé
   * directement (aucun rendu de plus), relu au défilement et au redimensionnement.
   */
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Le plan dépasse sa carte : phrase « Faites glisser… » sous la consigne. */
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const update = () => {
      const start = el.scrollLeft > 1;
      const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      const more = start && end ? 'both' : start ? 'start' : end ? 'end' : '';
      if (more) el.dataset.more = more;
      else delete el.dataset.more;
      setOverflowing(el.scrollWidth > el.clientWidth + 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(el);
    if (el.firstElementChild) observer?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [plan.cols, plan.rows]);

  /*
   * Plan qui défile : centré à l'arrivée et quand ses dimensions changent (autre largeur d'écran, autre plan) sur le
   * spot choisi, de l'invité ou réservé, à défaut sur le milieu de la salle.
   */
  const anchorSpot =
    plan.cells.find((c) => c.state === 'selected')?.spotNumber ??
    plan.cells.find((c) => c.state === 'guest')?.spotNumber ??
    plan.mySpots[0]?.spotNumber ??
    null;
  const anchorRef = useRef(anchorSpot);
  anchorRef.current = anchorSpot;
  const spotRect = (el: HTMLElement, n: number | null) => {
    const button = n === null ? null : el.querySelector<HTMLElement>(`.mmv2-spot[data-spot="${n}"]`);
    return button ? button.getBoundingClientRect() : null;
  };
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const center = () => {
      const left = centeredScrollLeft(
        { scrollLeft: el.scrollLeft, scrollWidth: el.scrollWidth, clientWidth: el.clientWidth, left: el.getBoundingClientRect().left },
        spotRect(el, anchorRef.current),
      );
      if (left !== null) el.scrollLeft = left;
    };
    center();
    // Largeur de la carte connue après la mise en page (polices, panneau) : recentré tant que le client n'a pas bougé
    // le plan lui-même.
    let touched = false;
    const onUser = () => {
      touched = true;
    };
    el.addEventListener('pointerdown', onUser, { passive: true });
    el.addEventListener('wheel', onUser, { passive: true });
    el.addEventListener('keydown', onUser);
    let lastWidth = el.clientWidth;
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            if (el.clientWidth === lastWidth) return;
            lastWidth = el.clientWidth;
            if (!touched) center();
          })
        : null;
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      el.removeEventListener('pointerdown', onUser);
      el.removeEventListener('wheel', onUser);
      el.removeEventListener('keydown', onUser);
    };
  }, [plan.cols, plan.rows]);

  /* Spot choisi (le sien ou celui de l'invité) à moitié caché sous un fondu : ramené au centre. */
  const selectedSpot = plan.cells.find((c) => c.state === 'selected')?.spotNumber ?? null;
  const guestSpot = plan.cells.find((c) => c.state === 'guest')?.spotNumber ?? null;
  const lastChoice = useRef({ selectedSpot, guestSpot });
  useEffect(() => {
    const previous = lastChoice.current;
    lastChoice.current = { selectedSpot, guestSpot };
    const changed =
      guestSpot !== null && guestSpot !== previous.guestSpot
        ? guestSpot
        : selectedSpot !== null && selectedSpot !== previous.selectedSpot
          ? selectedSpot
          : null;
    const el = scrollRef.current;
    if (changed === null || !el) return;
    const left = centeredScrollLeft(
      { scrollLeft: el.scrollLeft, scrollWidth: el.scrollWidth, clientWidth: el.clientWidth, left: el.getBoundingClientRect().left },
      spotRect(el, changed),
      true,
    );
    if (left === null) return;
    if (typeof el.scrollTo === 'function') el.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    else el.scrollLeft = left;
  }, [selectedSpot, guestSpot]);

  const isSelectable = (cell: PlanCell) =>
    interactive &&
    cell.kind !== 'coach' &&
    cell.kind !== 'other' &&
    !!cell.spotNumber &&
    (cell.state === 'free' || cell.state === 'selected' || cell.state === 'guest');
  // Ordre de lecture : rangée, puis colonne.
  const selectable = plan.cells
    .filter(isSelectable)
    .slice()
    .sort((a, b) => a.row - b.row || a.col - b.col);
  const tabStop =
    selectable.find((c) => c.spotNumber === focusedSpot) ??
    selectable.find((c) => c.state === 'selected') ??
    selectable.find((c) => c.state === 'guest') ??
    selectable[0];

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const current = selectable.findIndex((c) => String(c.spotNumber) === target.dataset.spot);
    if (current < 0 || !selectable.length) return;
    const here = selectable[current];
    let next: PlanCell | undefined;
    const vertical = (dir: 1 | -1) => {
      const rows = selectable.filter((c) => (dir > 0 ? c.row > here.row : c.row < here.row));
      if (!rows.length) return undefined;
      const row = dir > 0 ? Math.min(...rows.map((c) => c.row)) : Math.max(...rows.map((c) => c.row));
      return rows
        .filter((c) => c.row === row)
        .sort((a, b) => Math.abs(a.col - here.col) - Math.abs(b.col - here.col) || a.col - b.col)[0];
    };
    switch (event.key) {
      case 'ArrowRight':
        next = selectable[current + 1];
        break;
      case 'ArrowLeft':
        next = selectable[current - 1];
        break;
      case 'ArrowDown':
        next = vertical(1);
        break;
      case 'ArrowUp':
        next = vertical(-1);
        break;
      case 'Home':
        next = selectable[0];
        break;
      case 'End':
        next = selectable[selectable.length - 1];
        break;
      default:
        return;
    }
    event.preventDefault();
    if (!next) return;
    const button = event.currentTarget.querySelector<HTMLButtonElement>(`.mmv2-spot[data-spot="${next.spotNumber}"]`);
    button?.focus();
  };

  const renderCell = (cell: PlanCell) => {
    const style: CSSProperties = { gridRow: cell.row, gridColumn: `${cell.col} / span ${cell.colSpan}` };
    if (cell.kind === 'coach') {
      return (
        <div key={cell.key} className="mmv2-coach" style={style} title={coachName ? `Coach\u00a0: ${coachName}` : 'Coach'}>
          <span>{coachName || 'Coach'}</span>
        </div>
      );
    }
    if (cell.kind === 'other') {
      return <div key={cell.key} className="mmv2-room-other" style={style} aria-hidden="true" />;
    }
    const canSelect = isSelectable(cell);
    return (
      <button
        key={cell.key}
        type="button"
        className={`mmv2-spot mmv2-spot--${cell.shape}`}
        data-state={cell.state}
        data-spot={cell.spotNumber}
        style={style}
        disabled={!canSelect}
        tabIndex={canSelect ? (cell === tabStop ? 0 : -1) : undefined}
        aria-pressed={cell.state === 'selected' || cell.state === 'guest' ? true : canSelect ? false : undefined}
        aria-label={spotLabel(cell)}
        title={spotLabel(cell)}
        onFocus={canSelect ? () => setFocusedSpot(cell.spotNumber as number) : undefined}
        onClick={canSelect && onSpot && cell.spotNumber ? () => onSpot(cell.spotNumber as number) : undefined}
      >
        {cell.spotNumber}
      </button>
    );
  };

  return (
    <section className="mmv2-card mmv2-room-card" aria-labelledby="mmv2-room-title" ref={ref}>
      <div className="mmv2-room-head">
        <span className="mmv2-kicker">{kicker}</span>
        {capacityTag ? <span className={`mmv2-tag mmv2-tag--${capacityTag.tone}`}>{capacityTag.label}</span> : null}
      </div>
      <h2 className="mmv2-h3 mmv2-room-title" id="mmv2-room-title">
        {title}
      </h2>
      {hint ? <p className="mmv2-room-hint">{hint}</p> : null}
      {overflowing ? (
        <p className="mmv2-room-swipe">
          <Icon name="left" size={14} stroke={2} />
          Faites glisser le plan pour voir toute la salle
          <Icon name="right" size={14} stroke={2} />
        </p>
      ) : null}

      <div className="mmv2-room-scroll" ref={scrollRef}>
        <div
          className="mmv2-room"
          role="group"
          aria-label="Plan de salle"
          data-half={plan.half ? '' : undefined}
          style={{ '--mmv2-room-cols': plan.cols, '--mmv2-room-rows': plan.rows } as CSSProperties}
          onKeyDown={interactive ? onKeyDown : undefined}
        >
          {plan.cells.map(renderCell)}
        </div>
      </div>

      <ul className="mmv2-room-legend" aria-label="Légende du plan">
        {plan.showShapesLegend
          ? LEGEND_SHAPES.map((item) => (
              <li key={item.key}>
                <i className={`mmv2-legend-swatch mmv2-legend-swatch--${item.key}`} aria-hidden="true" />
                {item.label}
              </li>
            ))
          : null}
        <li>
          <i className="mmv2-legend-swatch" data-state="free" aria-hidden="true" />
          Libre
        </li>
        <li>
          <i className="mmv2-legend-swatch" data-state="taken" aria-hidden="true" />
          Pris
        </li>
        {interactive && showSelfLegend ? (
          <li>
            <i className="mmv2-legend-swatch" data-state="selected" aria-hidden="true" />
            Vous
          </li>
        ) : null}
        {showGuestLegend ? (
          <li>
            <i className="mmv2-legend-swatch" data-state="guest" aria-hidden="true" />
            Votre invité
          </li>
        ) : null}
        {hasMine ? (
          <li>
            <i className="mmv2-legend-swatch" data-state="mine" aria-hidden="true" />
            Votre réservation
          </li>
        ) : null}
      </ul>

      {note ? <p className="mmv2-room-note">{note}</p> : null}
      {frontRowNote ? <p className="mmv2-room-note">{frontRowNote}</p> : null}
    </section>
  );
});
